import React from "react";
import { X, Plus, ChevronUp, ChevronDown, Eye } from "lucide-react";
import CarrierSelector from "./CarrierSelector";
import TrialSearchLimitBanner from "./TrialSearchLimitBanner";
import SearchForm from "./SearchForm";
import ResultsToolbar from "./ResultsToolbar";
import ResultsTable from "./ResultsTable";
import BackhaulResults from "../../pages/BackhaulResults";
import MobileMenuButton from "../DashboardLayout/MobileMenuButton";

const NewLoadSearchView = ({
  form,
  tabs,
  activeTab,
  activeTabId,
  onTabChange,
  onAddTab,
  onRemoveTab,
  showSearchForm,
  onToggleSearchForm,
  carriers,
  selectedCarrier,
  onCarrierChange,
  isDispatcher,
  enableBackhaul,
  isTrialUser,
  trialCount,
  trialLimit,
  searchLimitReached,
  onUpgrade,
  isSearchLoading,
  isSSEPending,
  onSubmit,
  error,
  showSaved,
  onToggleSaved,
  onRefreshSaved,
  isFetchingSaved,
  isInternalUser,
  onDownloadCsv,
  sort,
  onSortChange,
  results,
  highlightMap = {},
  backhaulResults,
  resultsCount,
  showBackhaulView,
  onSelectLoad,
  handleGetCurrentLocation,
  searchParams,
  showPartners,
  onTogglePartners,
  viewedLoadIds = new Set(),
  isSelectMode = false,
  onToggleSelectMode,
  selectedLoadIds = new Set(),
  onToggleLoadSelection,
  onMarkSelectedAsViewed,
}) => {
  const selectedCount = selectedLoadIds.size;
  return (
    <div className="h-full flex flex-col bg-white">
      <div className="bg-tw-neutral-50 border-b border-tw-neutral-300 flex-shrink-0">
        <div className="flex items-center">
          {/* Static Hamburger Button on Mobile */}
          <div className="flex-shrink-0 px-3 py-2 lg:hidden">
            <MobileMenuButton />
          </div>
          
          {/* Scrollable Tabs Container */}
          <div className="flex-1 overflow-x-auto px-6 py-2 scrollbar-hide">
        <div className="flex items-center gap-2">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-t cursor-pointer text-sm transition-all flex-shrink-0 ${
                activeTabId === tab.id
                  ? "bg-white border-t-2 border-x border-tw-neutral-300 border-b-0 text-tw-neutral-900 shadow-sm font-semibold"
                  : "bg-tw-neutral-100 border border-tw-neutral-300 text-tw-neutral-500 hover:bg-tw-neutral-200 hover:text-tw-neutral-700"
              }`}
            >
                  <span className="font-medium whitespace-nowrap">
                {tab.formData.origin_city || "Origin"},{" "}
                {tab.formData.origin_state || "ST"}
              </span>
              <span className={activeTabId === tab.id ? "text-tw-neutral-600" : "text-tw-neutral-400"}>→</span>
                  <span className="font-medium whitespace-nowrap">
                {tab.formData.destination_city || "Destination"},{" "}
                {tab.formData.destination_state === ""
                  ? "Any"
                  : tab.formData.destination_state || "ST"}
              </span>
              {tabs.length > 1 && (
                <button
                  onClick={(event) => {
                    event.stopPropagation();
                    onRemoveTab(tab.id);
                  }}
                  className="ml-2 p-0.5 rounded hover:bg-tw-neutral-200 transition-colors focus:outline-none focus:ring-0"
                  aria-label="Close tab"
                >
                  <X className="h-3 w-3 text-tw-neutral-400 hover:text-tw-neutral-700" />
                </button>
              )}
            </div>
          ))}
          <button
            onClick={() => onAddTab()}
                className="px-2 py-1 bg-tw-info text-white rounded transition-colors flex-shrink-0"
          >
            <Plus className="h-4 w-4" />
          </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto flex flex-col">
        <div className="bg-white border-b border-gray-200 px-6 py-2 flex items-center justify-between flex-shrink-0">
          <button
            type="button"
            onClick={onToggleSearchForm}
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-tw-neutral-600 hover:text-tw-neutral-900 hover:bg-tw-neutral-100 rounded transition-all"
            aria-label={showSearchForm ? "Hide search form" : "Show search form"}
          >
            {showSearchForm ? (
              <>
                <ChevronUp className="h-3.5 w-3.5" />
                <span>Hide Search Form</span>
              </>
            ) : (
              <>
                <ChevronDown className="h-3.5 w-3.5" />
                <span>Show Search Form</span>
              </>
            )}
          </button>
        </div>

        {showSearchForm && (
          <>
            <CarrierSelector
              carriers={carriers}
              selectedCarrier={selectedCarrier}
              onChange={onCarrierChange}
              isDispatcher={isDispatcher}
            />

            {isTrialUser && (
              <TrialSearchLimitBanner
                count={trialCount}
                limit={trialLimit}
                limitReached={searchLimitReached}
                onUpgrade={onUpgrade}
              />
            )}

            <SearchForm
              form={form}
              onSubmit={onSubmit}
              onGetCurrentLocation={handleGetCurrentLocation}
              isSearchLoading={isSearchLoading}
              isTrialLimitReached={searchLimitReached}
            />
          </>
        )}

        {/* {error && (
          <div className="px-6 mt-3 flex items-center gap-2 text-tw-danger text-sm flex-shrink-0">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        )} */}

        <div className="flex-1 flex flex-col">
          {showBackhaulView ? (
            isSearchLoading ? (
              <div className="px-6 py-12 flex flex-col items-center justify-center">
                <div className="animate-spin h-8 w-8 border-4 border-tw-info border-t-transparent rounded-full mb-3" />
                <div className="text-sm text-tw-neutral-500">
                  Searching for loads...
                </div>
              </div>
            ) : backhaulResults.length > 0 ? (
              <BackhaulResults loadPairs={backhaulResults} searchParams={searchParams} />
            ) : (
              <div className="px-6 py-8 text-sm text-tw-neutral-500">
                No backhaul load pairs found matching your search criteria. Try
                adjusting your search filters.
              </div>
            )
          ) : (
            <>
              <ResultsToolbar
                showSavedLoads={showSaved}
                resultsCount={resultsCount}
                platformSummaryText={
                  enableBackhaul ? "Backhaul Search Enabled" : null
                }
                onToggleSavedLoads={onToggleSaved}
                onRefreshSavedLoads={onRefreshSaved}
                isFetchingSavedLoads={isFetchingSaved}
                isInternalUser={isInternalUser}
                onDownloadCsv={onDownloadCsv}
                sortOption={sort}
                onSortChange={(event) => onSortChange(event.target.value)}
                showPartners={showPartners}
                onTogglePartners={onTogglePartners}
                isSelectMode={isSelectMode}
                onToggleSelectMode={onToggleSelectMode}
              />

              <div className="bg-tw-neutral-50 overflow-x-auto scrollbar-hide">
                {showSaved && isFetchingSaved ? (
                  <div className="px-6 py-8 text-sm text-tw-neutral-500">
                    Loading saved loads...
                  </div>
                ) : !showSaved && isSearchLoading ? (
                  <div className="px-6 py-12 flex flex-col items-center justify-center">
                    <div className="animate-spin h-8 w-8 border-4 border-tw-info border-t-transparent rounded-full mb-3" />
                    <div className="text-sm text-tw-neutral-500">
                      {isSSEPending ? "Waiting for real-time results... If this takes longer than 30 seconds, press the '+' button to add another search tab and retry." : "Searching for loads..."}
                    </div>
                  </div>
                ) : resultsCount > 0 ? (
                  <ResultsTable
                    results={results}
                    onSelectLoad={onSelectLoad}
                    highlightMap={highlightMap}
                    showPartners={showPartners}
                    viewedLoadIds={viewedLoadIds}
                    isSelectMode={isSelectMode}
                    selectedLoadIds={selectedLoadIds}
                    onToggleLoadSelection={onToggleLoadSelection}
                  />
                ) : !showSaved && resultsCount === 0 && !isSearchLoading ? (
                  // Only show "No loads found" when search is complete with no results
                  <div className="px-6 py-8 text-sm text-tw-neutral-500">
                    No loads found matching your search criteria. Try adjusting your search filters.
                  </div>
                ) : showSaved && resultsCount === 0 && !isFetchingSaved ? (
                  // Show message for saved loads when none exist
                  <div className="px-6 py-8 text-sm text-tw-neutral-500">
                    No saved loads found. Save loads from your search results to review them here.
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Selection Action Bar - Fixed at bottom when loads are selected */}
      {isSelectMode && selectedCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 md:left-64 bg-white border-t border-tw-neutral-200 shadow-lg z-30 animate-slide-up">
          <div className="px-6 py-3 flex items-center justify-between">
            <span className="text-sm font-medium text-tw-neutral-700">
              {selectedCount} load{selectedCount === 1 ? "" : "s"} selected
            </span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onToggleSelectMode}
                className="px-3 py-1.5 text-xs font-medium rounded border border-tw-neutral-300 text-tw-neutral-700 hover:border-tw-neutral-400 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onMarkSelectedAsViewed}
                className="px-3 py-1.5 text-xs font-medium rounded bg-tw-info text-white hover:bg-tw-info/90 transition-colors flex items-center gap-1.5"
              >
                <Eye className="h-3.5 w-3.5" />
                <span>Mark as Viewed</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewLoadSearchView;

